import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import IProcessed_Report from "@models/IProcessed_Report";
import IOnGetUtmAttributionReportDTO from "@models/DTO/IOnGetUtmAttributionReportDTO";
import IOwTableCols from "@models/IOwTableCols";
import EndpointsEnum from "../../../../models/EndpointsEnum";
import IDownloadReportExcelDTO from "../../../../models/DTO/IDownloadReportExcelDTO";
import IOnDownloadReportExcelDTO from "../../../../models/DTO/IOnDownloadReportExcelDTO";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import ReportPeriod from "../ReportPeriod/ReportPeriod";
import { OwFnTable } from "../../../shared/OwFnTable/OwFnTable";
import "./UtmAttributionReport.scss";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    processedReport: IProcessed_Report;
};

// Humanize a raw column key for display, e.g. "utm_source" -> "Utm Source", "Order_Number" -> "Order Number".
const humanizeColumn = (key: string): string => {
    return key
        .split("_")
        .map((word) => (word.length ? word.charAt(0).toUpperCase() + word.slice(1) : word))
        .join(" ");
};

const formatCell = (value: any): string => {
    if (value === null || value === undefined) {
        return "";
    }

    if (typeof value === "number") {
        return value.toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    return String(value);
};

export const UtmAttributionReport = (props: Props) => {
    const { userModel, supplierModel, socket } = useAppContextNonNull();
    const intl = useIntl();

    const reportData = props.processedReport.report_data as IOnGetUtmAttributionReportDTO;

    const [downloadingExcel, setDownloadingExcel] = useState(false);

    useEffect(() => {
        socket.on(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);

        return () => {
            socket.removeListener(EndpointsEnum.ReportOnDownloadExcel, onDownloadExcel);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleDownloadExcel = () => {
        setDownloadingExcel(true);

        const payload: IDownloadReportExcelDTO = {
            userGuid: userModel.guid,
            userToken: userModel.token,
            supplierGuid: supplierModel.guid,
            reportGuid: props.processedReport.guid,
        };

        socket.emit(EndpointsEnum.ReportDownloadExcel, payload);
    };

    const onDownloadExcel = (data: IOnDownloadReportExcelDTO) => {
        setDownloadingExcel(false);

        if (data.downloadDirect && data.buffer) {
            downloadFromBase64(data.buffer, data.filename);
        } else if (data.s3Url) {
            window.open(data.s3Url, "_blank", "noopener,noreferrer");
        }
    };

    const downloadFromBase64 = (base64: string, filename: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    };

    const rows = reportData?.rows || [];

    // Columns are dynamic: the backend unions the utm_* keys present across the result set.
    // renderFnEmitsRow lets us read the raw value off the row so 0/empty values still render correctly.
    const columns: IOwTableCols[] = useMemo(() => {
        return (reportData?.columns || []).map((colName) => ({
            title: humanizeColumn(colName),
            valueProp: colName,
            skipTitleTranslate: true,
            renderFnEmitsRow: true,
            renderFn: (row: any) => formatCell(row[colName]),
        }));
    }, [reportData?.columns]);

    return (
        <div className="UtmAttributionReport">
            <ReportPeriod className={"mb-3"} startDate={reportData?.periodStart} endDate={reportData?.periodEnd} />
            {/* Header with Excel download */}
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <OwLabel
                        className="me-3"
                        title={downloadingExcel ? "generating_excel" : "excel"}
                        leftIconClass={downloadingExcel ? "fas fa-spinner fa-spin" : "fas fa-download"}
                        fn={handleDownloadExcel}
                    />
                </div>
                <div className="d-flex gap-4">
                    <span>
                        <strong>{intl.formatMessage({ id: "total_rows" })}:</strong> {rows.length}
                    </span>
                </div>
            </div>

            <OwFnTable
                columns={columns}
                dataSet={rows}
                dataFetch={false}
                initialData={false}
                tableConfig={{ tableStriped: true, tableHover: true, tableSmall: true }}
            />
        </div>
    );
};
